import { Drawer, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import useUser from 'utils/useUser';
import { useAccess } from 'infrastructure/hooks/access/useAccess';

import { ClinicalDocButtons } from './clinical-doc-buttons';
import { RequestButtons } from './request-buttons';
import { StatusButtons } from './status-buttons';
import { ReportButtons } from './report-buttons';
import s from './styles.module.scss';

import type { ModuleType } from 'infrastructure/enums';
import type { FC } from 'react';

interface PatientActionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  patientGuid: string;
  module: ModuleType;
  clinicGuid?: string;
  emailSubscription?: boolean;
  ccmHoldingStatus?: boolean;
  disabledCreateInternalAdmissionForms?: boolean;
}

const PatientActionsDrawer: FC<PatientActionsDrawerProps> = ({
  isOpen,
  onClose,
  patientGuid,
  module,
  clinicGuid,
  emailSubscription,
  ccmHoldingStatus,
  disabledCreateInternalAdmissionForms,
}) => {
  const { t } = useTranslation();

  const { isAdmin, isAgencyDoctor, isPractice } = useUser();

  const { isCombinedModule, isRpmModule } = useAccess({
    module,
    patientGuid,
  });

  const hasVisibleClinicalButtons =
    ((isRpmModule || isCombinedModule) && !isPractice) ||
    isAdmin ||
    isAgencyDoctor;

  return (
    <Drawer
      title={t('labels.controlPanel', 'Control Panel')}
      placement="right"
      open={isOpen}
      onClose={onClose}
      width={378}
      mask={false}
      zIndex={1011}
      className={s.drawer}
      rootStyle={{ right: 0, bottom: 0, top: 0 }}
      styles={{
        wrapper: {
          top: 8,
          bottom: 8,
          right: 8,
          borderRadius: 8,
          overflow: 'hidden',
        },
      }}
    >
      {hasVisibleClinicalButtons && (
        <>
          <div className={s.section}>
            <h3 className={s['section-title']}>
              {t('labels.clinicalDocumentationActions')}
            </h3>
            <div className={s['docs-list']}>
              <ClinicalDocButtons
                patientGuid={patientGuid}
                module={module}
                clinicGuid={clinicGuid}
              />
            </div>
          </div>

          <Divider className={s.divider} />
        </>
      )}

      <div className={s.section}>
        <h3 className={s['section-title']}>{t('labels.requestLabel')}</h3>
        <div className={s['docs-list']}>
          <RequestButtons patientGuid={patientGuid} module={module} />
        </div>
      </div>

      <Divider className={s.divider} />

      <div className={s.section}>
        <h3 className={s['section-title']}>{t('labels.statusLabel')}</h3>
        <StatusButtons
          patientGuid={patientGuid}
          module={module}
          emailSubscription={emailSubscription}
          ccmHoldingStatus={ccmHoldingStatus}
        />
      </div>

      <Divider className={s.divider} />

      <div className={s.section}>
        <ReportButtons
          patientGuid={patientGuid}
          disabledCreateInternalAdmissionForms={
            disabledCreateInternalAdmissionForms
          }
        />
      </div>
    </Drawer>
  );
};

export default PatientActionsDrawer;
